import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft, Plus, Search, Users, DollarSign, Star, Phone, Mail, X,
  MessageSquare, Calendar, Building, Trash2, Edit2, Save, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SEGMENTS = ['All', 'Wedding', 'Corporate', 'VIP', 'Airport', 'Event', 'General'];
const VIP_LEVELS = ['All', 'Standard', 'VIP', 'VIP2', 'VIP3'];
const COMM_TYPES = ['call', 'email', 'text', 'note', 'follow_up'];

const ClientCRM = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [kpis, setKpis] = useState({});
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('All');
  const [vipFilter, setVipFilter] = useState('All');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [commMessage, setCommMessage] = useState('');
  const [commType, setCommType] = useState('note');

  const loadClients = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (segmentFilter !== 'All') params.set('segment', segmentFilter);
    if (vipFilter !== 'All') params.set('vip_level', vipFilter);
    const res = await fetch(`${API}/api/crm/clients?${params}`);
    const data = await res.json();
    setClients(data.clients || []);
    setKpis(data.kpis || {});
  }, [search, segmentFilter, vipFilter]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadClient = async (clientId) => {
    const res = await fetch(`${API}/api/crm/clients/${clientId}`);
    const data = await res.json();
    setSelectedClient(data);
  };

  const saveClient = async () => {
    const url = formData.client_id
      ? `${API}/api/crm/clients/${formData.client_id}`
      : `${API}/api/crm/clients`;
    const method = formData.client_id ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    if (res.ok) {
      toast.success(formData.client_id ? 'Client updated' : 'Client added');
      setShowForm(false);
      setFormData({});
      loadClients();
    } else { toast.error('Failed to save client'); }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Delete this client?')) return;
    await fetch(`${API}/api/crm/clients/${clientId}`, { method: 'DELETE' });
    toast.success('Client removed');
    setSelectedClient(null);
    loadClients();
  };

  const addComm = async () => {
    if (!commMessage.trim() || !selectedClient) return;
    await fetch(`${API}/api/crm/communications`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_id: selectedClient.client_id, type: commType, message: commMessage })
    });
    setCommMessage('');
    toast.success('Log added');
    loadClient(selectedClient.client_id);
  };

  const f = (key, val) => setFormData(p => ({ ...p, [key]: val }));

  return (
    <div className="min-h-screen bg-gray-100" data-testid="client-crm-page">
      {/* Header */}
      <header className="bg-black text-white py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-300 hover:text-white flex-shrink-0" data-testid="crm-back-btn">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold truncate">Client CRM</h1>
              <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">Manage your client relationships</p>
            </div>
          </div>
          <Button onClick={() => { setFormData({}); setShowForm(true); }} size="sm" className="bg-red-600 hover:bg-red-700 flex-shrink-0" data-testid="add-client-btn">
            <Plus className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Add Client</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4" data-testid="kpi-total-clients">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center"><Users className="h-5 w-5 text-blue-600" /></div>
              <div><p className="text-2xl font-bold">{kpis.total_clients || 0}</p><p className="text-xs text-gray-500">Total Clients</p></div>
            </div>
          </Card>
          <Card className="p-4" data-testid="kpi-ltv">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center"><DollarSign className="h-5 w-5 text-green-600" /></div>
              <div><p className="text-2xl font-bold">${(kpis.total_ltv || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Total LTV</p></div>
            </div>
          </Card>
          <Card className="p-4" data-testid="kpi-vip">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center"><Star className="h-5 w-5 text-yellow-600" /></div>
              <div><p className="text-2xl font-bold">{(kpis.vip_counts?.VIP || 0) + (kpis.vip_counts?.VIP2 || 0) + (kpis.vip_counts?.VIP3 || 0)}</p><p className="text-xs text-gray-500">VIP Clients</p></div>
            </div>
          </Card>
          <Card className="p-4" data-testid="kpi-segments">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center"><Building className="h-5 w-5 text-purple-600" /></div>
              <div><p className="text-2xl font-bold">{Object.keys(kpis.segments || {}).length}</p><p className="text-xs text-gray-500">Segments</p></div>
            </div>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Client List */}
          <div className="lg:col-span-2">
            <Card className="p-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="crm-search" />
                  </div>
                </div>
                <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="segment-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEGMENTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={vipFilter} onValueChange={setVipFilter}>
                  <SelectTrigger className="w-[120px]" data-testid="vip-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>{VIP_LEVELS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="clients-table">
                  <thead>
                    <tr className="border-b text-left text-sm text-gray-500">
                      <th className="py-2 px-3">Name</th>
                      <th className="py-2 px-3">Segment</th>
                      <th className="py-2 px-3">VIP</th>
                      <th className="py-2 px-3">Spent</th>
                      <th className="py-2 px-3">Bookings</th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map(c => (
                      <tr key={c.client_id} className={`border-b hover:bg-gray-50 cursor-pointer ${selectedClient?.client_id === c.client_id ? 'bg-red-50' : ''}`}
                        onClick={() => loadClient(c.client_id)} data-testid={`client-row-${c.client_id}`}>
                        <td className="py-3 px-3">
                          <div className="font-medium text-sm">{c.name}</div>
                          <div className="text-xs text-gray-400">{c.email || c.phone}</div>
                        </td>
                        <td className="py-3 px-3"><span className="text-xs bg-gray-100 px-2 py-1 rounded">{c.segment}</span></td>
                        <td className="py-3 px-3"><span className={`text-xs px-2 py-1 rounded ${c.vip_level !== 'Standard' ? 'bg-yellow-100 text-yellow-800 font-semibold' : 'text-gray-500'}`}>{c.vip_level}</span></td>
                        <td className="py-3 px-3 text-sm font-medium">${(c.total_spent || 0).toLocaleString()}</td>
                        <td className="py-3 px-3 text-sm">{c.total_bookings || 0}</td>
                        <td className="py-3 px-3"><ChevronRight className="h-4 w-4 text-gray-400" /></td>
                      </tr>
                    ))}
                    {clients.length === 0 && (
                      <tr><td colSpan={6} className="py-12 text-center text-gray-400">No clients yet. Click "Add Client" to get started.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Client Detail Panel */}
          <div>
            {selectedClient ? (
              <Card className="p-5" data-testid="client-detail">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">{selectedClient.name}</h3>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setFormData(selectedClient); setShowForm(true); }}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteClient(selectedClient.client_id)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  {selectedClient.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gray-400" />{selectedClient.email}</p>}
                  {selectedClient.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gray-400" />{selectedClient.phone}</p>}
                  {selectedClient.company && <p className="flex items-center gap-2"><Building className="h-3.5 w-3.5 text-gray-400" />{selectedClient.company}</p>}
                  <div className="flex gap-2 pt-2">
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">{selectedClient.segment}</span>
                    <span className={`text-xs px-2 py-1 rounded ${selectedClient.vip_level !== 'Standard' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100'}`}>{selectedClient.vip_level}</span>
                  </div>
                  {selectedClient.notes && <p className="text-gray-500 pt-2 text-xs">{selectedClient.notes}</p>}
                </div>

                <div className="border-t pt-4 mb-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-xl font-bold text-green-700">${(selectedClient.total_spent || 0).toLocaleString()}</p>
                      <p className="text-xs text-green-600">Total Spent</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-xl font-bold text-blue-700">{selectedClient.total_bookings || 0}</p>
                      <p className="text-xs text-blue-600">Bookings</p>
                    </div>
                  </div>
                </div>

                {/* Communication Log */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-bold mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4" />Communication Log</h4>
                  <div className="flex gap-2 mb-3">
                    <Select value={commType} onValueChange={setCommType}>
                      <SelectTrigger className="w-[100px] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{COMM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input placeholder="Add note..." value={commMessage} onChange={e => setCommMessage(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addComm()} className="h-8 text-xs flex-1" data-testid="comm-input" />
                    <Button size="sm" onClick={addComm} className="h-8 bg-black"><Plus className="h-3 w-3" /></Button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(selectedClient.communications || []).map((log, i) => (
                      <div key={i} className="bg-gray-50 p-2 rounded text-xs">
                        <div className="flex justify-between">
                          <span className="font-medium capitalize">{log.type}</span>
                          <span className="text-gray-400">{new Date(log.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-gray-600 mt-1">{log.message}</p>
                      </div>
                    ))}
                    {(!selectedClient.communications || selectedClient.communications.length === 0) && (
                      <p className="text-xs text-gray-400 text-center py-4">No communication logs yet</p>
                    )}
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="p-8 text-center text-gray-400">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Select a client to view details</p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Client Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" data-testid="client-form-modal">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">{formData.client_id ? 'Edit Client' : 'Add Client'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-4">
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={e => f('name', e.target.value)} data-testid="form-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email</Label><Input value={formData.email || ''} onChange={e => f('email', e.target.value)} data-testid="form-email" /></div>
                <div><Label>Phone</Label><Input value={formData.phone || ''} onChange={e => f('phone', e.target.value)} data-testid="form-phone" /></div>
              </div>
              <div><Label>Company</Label><Input value={formData.company || ''} onChange={e => f('company', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Segment</Label>
                  <Select value={formData.segment || 'General'} onValueChange={v => f('segment', v)}>
                    <SelectTrigger data-testid="form-segment"><SelectValue /></SelectTrigger>
                    <SelectContent>{SEGMENTS.slice(1).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>VIP Level</Label>
                  <Select value={formData.vip_level || 'Standard'} onValueChange={v => f('vip_level', v)}>
                    <SelectTrigger data-testid="form-vip"><SelectValue /></SelectTrigger>
                    <SelectContent>{VIP_LEVELS.slice(1).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Referral Source</Label><Input value={formData.referral_source || ''} onChange={e => f('referral_source', e.target.value)} /></div>
              <div><Label>Notes</Label><textarea value={formData.notes || ''} onChange={e => f('notes', e.target.value)} className="w-full border rounded-md p-2 text-sm h-20" /></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={saveClient} className="bg-red-600 hover:bg-red-700" data-testid="save-client-btn">
                <Save className="h-4 w-4 mr-2" />{formData.client_id ? 'Update' : 'Add'} Client
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ClientCRM;
